import React, { useState } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { siteImages, CONTACT_INFO } from '../data/mock';
import { Mail, Phone, MapPin, Send, Clock, CheckCircle, Building2, Heart, MessageCircle } from 'lucide-react';
import usePageTitle from '../hooks/usePageTitle';

const ContactPage = () => {
  usePageTitle(
    'Contact Stitch Your Dash | Customer Support',
    `Get in touch with Stitch Your Dash. Call ${CONTACT_INFO.phone} or email ${CONTACT_INFO.email} for questions about sewing tools and orders.`
  );

  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setFormData({ name: '', email: '', subject: '', message: '' });
    }, 3000);
  };

  return (
    <div className="min-h-screen bg-warmWhite-100">
      <Header />
      <CartDrawer />

      <main className="pt-[64px] md:pt-[105px] lg:pt-[125px]">
        {/* Banner */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0">
            <img src={siteImages.contactBanner} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 gradient-banner-overlay" />
          </div>
          <div className="relative container-wide py-20 lg:py-28 text-cream-50">
            <p className="eyebrow text-rose-200 mb-4" style={{color: '#f5d2c6'}}><Heart className="w-3.5 h-3.5" /> We’d Love to Hear from You</p>
            <h1 className="font-display text-editorial-lg leading-[1.05] max-w-3xl">
              Say <em className="italic-display text-rose-200 not-italic" style={{fontStyle:'italic'}}>hello</em>.
            </h1>
            <p className="mt-5 max-w-xl text-cream-200/85">
              Real, friendly support — ready to help with orders, products, or your next creative project.
            </p>
          </div>
        </section>

        {/* Content */}
        <section className="py-16 lg:py-24">
          <div className="container-wide grid lg:grid-cols-12 gap-10 lg:gap-16">
            {/* Left: contact cards */}
            <div className="lg:col-span-5 space-y-4">
              <h2 className="font-display text-editorial-sm text-warmBrown-950 mb-2">
                We respond within <em className="italic-display text-rose-600 not-italic" style={{fontStyle:'italic'}}>24 hours</em>.
              </h2>
              <p className="text-warmBrown-700 leading-relaxed mb-6">
                Send us a message, give us a call, or drop by our virtual studio — we’re here for you.
              </p>

              <a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`} className="flex items-start gap-4 p-5 rounded-[24px] bg-warmWhite-100 border border-cream-200 hover:border-rose-200 hover:shadow-card transition-all group">
                <span className="w-12 h-12 rounded-2xl bg-rose-100 text-rose-600 flex items-center justify-center group-hover:bg-rose-500 group-hover:text-white transition-colors flex-shrink-0">
                  <Phone className="w-5 h-5" />
                </span>
                <span>
                  <span className="block text-[11px] uppercase tracking-[0.18em] text-warmBrown-500 mb-1">Phone</span>
                  <span className="block font-display text-xl text-warmBrown-950">{CONTACT_INFO.phone}</span>
                  <span className="block text-xs text-warmBrown-500 mt-1">Mon-Fri 9am-6pm EST</span>
                </span>
              </a>

              <a href={`mailto:${CONTACT_INFO.email}`} className="flex items-start gap-4 p-5 rounded-[24px] bg-warmWhite-100 border border-cream-200 hover:border-rose-200 hover:shadow-card transition-all group">
                <span className="w-12 h-12 rounded-2xl bg-rose-100 text-rose-600 flex items-center justify-center group-hover:bg-rose-500 group-hover:text-white transition-colors flex-shrink-0">
                  <Mail className="w-5 h-5" />
                </span>
                <span>
                  <span className="block text-[11px] uppercase tracking-[0.18em] text-warmBrown-500 mb-1">Email</span>
                  <span className="block text-warmBrown-950">{CONTACT_INFO.email}</span>
                  <span className="block text-xs text-warmBrown-500 mt-1">We reply within a day</span>
                </span>
              </a>

              <div className="flex items-start gap-4 p-5 rounded-[24px] bg-warmWhite-100 border border-cream-200">
                <span className="w-12 h-12 rounded-2xl bg-rose-100 text-rose-600 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5" />
                </span>
                <span>
                  <span className="block text-[11px] uppercase tracking-[0.18em] text-warmBrown-500 mb-1">Corporate Address</span>
                  <span className="block text-warmBrown-950 leading-relaxed">
                    {CONTACT_INFO.address.street}<br />
                    {CONTACT_INFO.address.city}, {CONTACT_INFO.address.state} {CONTACT_INFO.address.zip}<br />
                    {CONTACT_INFO.address.country}
                  </span>
                </span>
              </div>

              <div className="flex items-start gap-4 p-5 rounded-[24px] bg-warmBrown-900 text-cream-50">
                <span className="w-12 h-12 rounded-2xl bg-cream-50/10 text-cream-50 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-5 h-5" />
                </span>
                <span>
                  <span className="block text-[11px] uppercase tracking-[0.18em] text-cream-200/75 mb-1">Operated by</span>
                  <span className="block font-display text-lg">{CONTACT_INFO.corporateName}</span>
                </span>
              </div>

              <div className="p-5 rounded-[24px] bg-rose-50 border border-rose-100">
                <div className="flex items-center gap-3 mb-3">
                  <Clock className="w-5 h-5 text-rose-600" />
                  <h3 className="font-display text-lg text-warmBrown-950">Business Hours</h3>
                </div>
                <ul className="text-sm text-warmBrown-700 space-y-1">
                  <li>Monday – Friday: 9am – 6pm EST</li>
                  <li>Saturday: 10am – 4pm EST</li>
                  <li>Sunday: Closed</li>
                </ul>
              </div>
            </div>

            {/* Right: form */}
            <div className="lg:col-span-7">
              <div className="bg-warmWhite-100 rounded-[36px] p-6 lg:p-12 shadow-card border border-cream-200">
                <p className="eyebrow mb-3"><MessageCircle className="w-3.5 h-3.5" /> Send us a note</p>
                <h3 className="font-display text-editorial-sm text-warmBrown-950 mb-2">
                  Drop a <em className="italic-display text-rose-600 not-italic" style={{fontStyle:'italic'}}>message</em>.
                </h3>
                <p className="text-warmBrown-600 mb-8">Fill in a quick note and we’ll get back to you soon.</p>

                {submitted ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="w-16 h-16 rounded-full bg-rose-100 flex items-center justify-center mb-4">
                      <CheckCircle className="w-8 h-8 text-rose-600" />
                    </div>
                    <h4 className="font-display text-2xl text-warmBrown-950 mb-2">Message sent!</h4>
                    <p className="text-warmBrown-600">Thank you. We’ll be in touch shortly.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs uppercase tracking-[0.16em] text-warmBrown-600 mb-2 font-medium">Name</label>
                        <input
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="w-full px-4 py-3.5 rounded-2xl bg-cream-50 border border-cream-200 focus:border-rose-300 transition-all"
                          placeholder="Your name"
                        />
                      </div>
                      <div>
                        <label className="block text-xs uppercase tracking-[0.16em] text-warmBrown-600 mb-2 font-medium">Email</label>
                        <input
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="w-full px-4 py-3.5 rounded-2xl bg-cream-50 border border-cream-200 focus:border-rose-300 transition-all"
                          placeholder="you@email.com"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-[0.16em] text-warmBrown-600 mb-2 font-medium">Subject</label>
                      <input
                        type="text"
                        required
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        className="w-full px-4 py-3.5 rounded-2xl bg-cream-50 border border-cream-200 focus:border-rose-300 transition-all"
                        placeholder="How can we help?"
                      />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-[0.16em] text-warmBrown-600 mb-2 font-medium">Message</label>
                      <textarea
                        rows={5}
                        required
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        className="w-full px-4 py-3.5 rounded-2xl bg-cream-50 border border-cream-200 focus:border-rose-300 transition-all resize-none"
                        placeholder="Tell us a little more..."
                      />
                    </div>
                    <button
                      type="submit"
                      className="w-full inline-flex items-center justify-center gap-2 py-4 bg-warmBrown-900 hover:bg-rose-600 text-white font-medium rounded-full transition-colors shadow-soft"
                    >
                      Send message
                      <Send className="w-4 h-4" />
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;
